import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { GetCustomerBrandResponse, GetCustomerDetailsResponse, GetCustomerFeatureResponse, GetCustomerResponseBrand } from "../models/Customer";
import { useAuth } from "../contexts/AuthContext";

export default function useCustomerDetails() {
  const { get } = useApiHelper();
  const { loggedIn, username } = useAuth();
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60 * 10; // 10 minutes

  const customer = useQuery(
    ["customer", "details"],
    () => {
      //check for local storage first
      const cachedCustomer = localStorage.getItem("customerDetails");
      const cachedCustomerExp = localStorage.getItem("customerDetailsExp");

      if (cachedCustomer && cachedCustomer !== "undefined" && cachedCustomerExp && Date.now() < parseInt(cachedCustomerExp)) {
        return JSON.parse(cachedCustomer) as GetCustomerDetailsResponse;
      } else {
        var customerDetails = get<GetCustomerDetailsResponse>(`/customer`)
          .then((data) => {
            // Store in localStorage for 10 minutes
            localStorage.setItem("customerDetails", JSON.stringify(data));
            localStorage.setItem("customerDetailsExp", (Date.now() + staleTime).toString());
            return data;
          });
        return customerDetails;
      }
    },
    {
      staleTime: staleTime,
      refetchInterval: staleTime,
      refetchIntervalInBackground: true,
      enabled: loggedIn,
    }
  );

  const features = useQuery(
    ["customer", "features"],
    () => {
      //check for local storage first
      const cachedFeatures = localStorage.getItem("customerFeatures");
      const cachedFeaturesExp = localStorage.getItem("customerFeaturesExp");

      if (cachedFeatures && cachedFeatures !== "undefined" && cachedFeaturesExp && Date.now() < parseInt(cachedFeaturesExp)) {
        return JSON.parse(cachedFeatures) as GetCustomerFeatureResponse;
      } else {
        var customerFeatures = get<GetCustomerFeatureResponse>(`/customer/features`)
          .then((data) => {
            // Store in localStorage for 10 minutes
            localStorage.setItem("customerFeatures", JSON.stringify(data));
            localStorage.setItem("customerFeaturesExp", (Date.now() + staleTime).toString());
            return data;
          });
        return customerFeatures;
      }
    },
    {
      staleTime: staleTime,
      refetchInterval: staleTime,
      refetchIntervalInBackground: true,
      enabled: loggedIn,
    }
  );

  const brand = useQuery(
    ["customer", "brand"],
    () => {
      //check for local storage first
      const cachedBrand = localStorage.getItem("customerBrand");
      const cachedBrandExp = localStorage.getItem("customerBrandExp");

      if (cachedBrand && cachedBrand !== "undefined" && cachedBrandExp && Date.now() < parseInt(cachedBrandExp)) {
        return JSON.parse(cachedBrand) as GetCustomerBrandResponse;
      } else {
        var customerBrand = get<GetCustomerBrandResponse>(`/customer/brand`)
          .then((data) => {
            // Store in localStorage for 10 minutes
            localStorage.setItem("customerBrand", JSON.stringify(data));
            localStorage.setItem("customerBrandExp", (Date.now() + staleTime).toString());
            return data;
          });
        return customerBrand;
      }
    },
    {
      staleTime: staleTime,
      refetchInterval: staleTime,
      refetchIntervalInBackground: true,
      enabled: loggedIn,
      // enabled: false,
    }
  );

  const publicBrand = useQuery(
    ["customer", "brand", "public"],
    () => {
      //check for local storage first
      const cachedPublicBrand = localStorage.getItem("customerPublicBrand");
      const cachedPublicBrandExp = localStorage.getItem("customerPublicBrandExp");

      if (cachedPublicBrand && cachedPublicBrand !== "undefined" && cachedPublicBrandExp && Date.now() < parseInt(cachedPublicBrandExp)) {
        return JSON.parse(cachedPublicBrand) as GetCustomerBrandResponse;
      } else {
        var customerPublicBrand = get<GetCustomerBrandResponse>(`/customer/brand/public`)
          .then((data) => {
            // Store in localStorage for 10 minutes
            localStorage.setItem("customerPublicBrand", JSON.stringify(data));
            localStorage.setItem("customerPublicBrandExp", (Date.now() + staleTime).toString());
            return data;
          });
        return customerPublicBrand;
      }
    },
    {
      staleTime: staleTime,
      refetchInterval: staleTime,
      refetchIntervalInBackground: true,
      // enabled: false,
    }
  );


  return {
    customer,
    features,
    brand,
    publicBrand
  };
}
